import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** How long a still press must last before it becomes a hold. */
export const HOLD_THRESHOLD_MS = 350
/** Movement beyond this (px, either axis) before the hold starts cancels it: it is a scroll or a swipe. */
export const HOLD_MOVE_TOLERANCE_PX = 10
/**
 * Presses that start on these never become a hold: the controls, the seek
 * slider and the speed menu own their own pointer handling.
 */
export const HOLD_IGNORED_TARGETS =
  'button, a, input, select, textarea, [role="slider"], [role="menu"], [role="menuitemradio"]'

export interface PressAndHoldOptions {
  /** Presses are ignored while false; turning it false mid-press ends the press and any hold. */
  enabled: boolean
  holdMs?: number
  moveTolerancePx?: number
}

export interface PressAndHoldHandlers {
  onPointerDown: (e: PointerEvent) => void
  onPointerMove: (e: PointerEvent) => void
  onPointerUp: (e: PointerEvent) => void
  onPointerCancel: (e: PointerEvent) => void
  onLostPointerCapture: (e: PointerEvent) => void
  onContextMenu: (e: Event) => void
}

export interface PressAndHold {
  isHolding: boolean
  handlers: PressAndHoldHandlers
  /**
   * True exactly once, for the `click` the browser dispatches right after a
   * hold ends on `pointerup` (or after a hold that `enabled → false` cut short
   * is released). The caller swallows that click so releasing a hold never
   * toggles playback or advances a stories stage.
   *
   * The flag is not timed: it stays armed until the next click consumes it or
   * the next `pointerdown` clears it, so a release whose click never arrives
   * cannot swallow a later, unrelated tap.
   */
  consumeSuppressedClick: () => boolean
}

interface PressState {
  pointerId: number
  pointerType: string
  startX: number
  startY: number
  captureTarget: Element | null
}

function releaseCapture(press: PressState) {
  try {
    if (press.captureTarget?.hasPointerCapture?.(press.pointerId)) {
      press.captureTarget.releasePointerCapture(press.pointerId)
    }
  } catch {
    // The pointer is already gone; nothing to release.
  }
}

/**
 * A long-press state machine for one element.
 *
 * It never calls `preventDefault` on pointer events and does not require
 * `touch-action: none`, so a touch that starts on the element still scrolls
 * the page: the browser takes over the gesture, fires `pointercancel`, and the
 * pending press (or active hold) ends.
 */
export function usePressAndHold({
  enabled,
  holdMs = HOLD_THRESHOLD_MS,
  moveTolerancePx = HOLD_MOVE_TOLERANCE_PX,
}: PressAndHoldOptions): PressAndHold {
  const [isHolding, setIsHolding] = useState(false)
  const pressRef = useRef<PressState | null>(null)
  const holdingRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const suppressClickRef = useRef(false)
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const endHold = useCallback(() => {
    if (!holdingRef.current) return
    holdingRef.current = false
    setIsHolding(false)
  }, [])

  /** Ends the press. Returns whether a hold was active. */
  const finishPress = useCallback((): boolean => {
    clearTimer()
    const press = pressRef.current
    pressRef.current = null
    if (press) releaseCapture(press)
    const wasHolding = holdingRef.current
    endHold()
    return wasHolding
  }, [clearTimer, endHold])

  const handlePointerDown = useCallback((e: PointerEvent) => {
    if (!enabledRef.current) return
    // A second finger (or pen) during a press is not a new press.
    if (e.isPrimary === false) return
    if (e.button !== undefined && e.button !== 0) return
    const target = e.target
    if (target instanceof Element && target.closest(HOLD_IGNORED_TARGETS)) return

    finishPress()
    suppressClickRef.current = false

    const captureTarget = e.currentTarget instanceof Element ? e.currentTarget : null
    pressRef.current = {
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      startX: e.clientX,
      startY: e.clientY,
      captureTarget,
    }
    try {
      captureTarget?.setPointerCapture?.(e.pointerId)
    } catch {
      // Capture is a nicety (keeps pointerup coming if the finger drifts off);
      // the press still works without it.
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      if (!pressRef.current) return
      holdingRef.current = true
      setIsHolding(true)
    }, holdMs)
  }, [finishPress, holdMs])

  const handlePointerMove = useCallback((e: PointerEvent) => {
    const press = pressRef.current
    if (!press || press.pointerId !== e.pointerId) return
    // Once the hold is on, the finger may wander; only a pending press is a
    // candidate for a scroll or swipe.
    if (holdingRef.current) return
    const dx = Math.abs(e.clientX - press.startX)
    const dy = Math.abs(e.clientY - press.startY)
    if (dx > moveTolerancePx || dy > moveTolerancePx) finishPress()
  }, [finishPress, moveTolerancePx])

  const handlePointerUp = useCallback((e: PointerEvent) => {
    const press = pressRef.current
    if (!press || press.pointerId !== e.pointerId) return
    // Arm suppression for the click that follows this pointerup. It is
    // consumed by that click or cleared by the next pointerdown.
    if (finishPress()) suppressClickRef.current = true
  }, [finishPress])

  const handlePointerEnd = useCallback((e: PointerEvent) => {
    const press = pressRef.current
    if (!press || press.pointerId !== e.pointerId) return
    // No click follows a cancel, so nothing to suppress.
    finishPress()
  }, [finishPress])

  const handleContextMenu = useCallback((e: Event) => {
    // A long touch opens the context menu / "save video" sheet on Android.
    // Mouse right-click stays the browser's.
    const press = pressRef.current
    if (!press || press.pointerType === 'mouse') return
    e.preventDefault()
  }, [])

  useEffect(() => {
    if (enabled) return
    // A hold cut short here (the video was paused or scrolled away) still
    // ends with the finger lifting, and that click must not count as a tap.
    if (finishPress()) suppressClickRef.current = true
  }, [enabled, finishPress])

  useEffect(() => () => { finishPress() }, [finishPress])

  const consumeSuppressedClick = useCallback((): boolean => {
    const suppressed = suppressClickRef.current
    suppressClickRef.current = false
    return suppressed
  }, [])

  const handlers = useMemo<PressAndHoldHandlers>(() => ({
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerEnd,
    onLostPointerCapture: handlePointerEnd,
    onContextMenu: handleContextMenu,
  }), [handleContextMenu, handlePointerDown, handlePointerEnd, handlePointerMove, handlePointerUp])

  return { isHolding, handlers, consumeSuppressedClick }
}
